import { t } from '@lingui/macro';
import React, { useContext, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';

import Building from '@decisiv/iconix/lib/components/Building';
import BarChart from '@decisiv/iconix/lib/components/BarChart';
import Home from '@decisiv/iconix/lib/components/Home';
import List from '@decisiv/iconix/lib/components/List';
import Truck from '@decisiv/iconix/lib/components/Truck';
import Calendar from '@decisiv/iconix/lib/components/Calendar';
import Cog from '@decisiv/iconix/lib/components/Cog';
import UsersGroup from '@decisiv/iconix/lib/components/UsersGroup';
import Folder from '@decisiv/iconix/lib/components/Folder';
import Grid from '@decisiv/iconix/lib/components/Grid';
import User from '@decisiv/iconix/lib/components/User';

import LeftNav, {
  useStickyLeftNav,
} from '@decisiv/ui-components/lib/components/LeftNav';

import TopNav from 'components/TopNav';

import ROUTE_NAMES from 'setup/Routing/routeNames';

import useSentinelTeam from 'hooks/useSentinelTeam';
import useTeamPath from 'hooks/useTeamPath';

import { useCurrentUserContext } from 'utils/User';
import { CurrentInvitationContext } from 'utils/Invitation';
import useFeatureFlag, { FEATURE_FLAGS } from 'utils/useFeatureFlag';
import { stripTeamPrefix } from 'utils/teamFromUrl';

import { Outlet, useLocation } from 'react-router-dom';
import FaviconManager from 'components/FaviconManager';
import ScrollingContent from './ScrollingContent';
import Versions from './Versions';

import {
  LeftNavContainer,
  NavColumnContainer,
  PageContainer,
  PrimaryContentContainer,
  TopNavContainer,
} from './styles';
import useCaseAdminUrlsQuery from './useCaseAdminUrlsQuery';

const fleetAssetGroupingItem = {
  icon: Building,
  text: t`Depots`,
  path: ROUTE_NAMES.depots,
};
const customerAssetGroupingItem = {
  icon: Building,
  text: t`Customers`,
  path: ROUTE_NAMES.customers,
};

function Layout() {
  const { authenticationKey } = useContext(CurrentInvitationContext);
  const location = useLocation();
  const { teamKey: urlTeamKey, teamPath } = useTeamPath();

  const assetViewsEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_ASSET_VIEWS);
  const pd1188FFEnabled = useFeatureFlag(
    FEATURE_FLAGS.PD_1188_ROUTE_BASED_FLEET_MANAGEMENT,
  );

  const isManagementSection =
    pd1188FFEnabled &&
    stripTeamPrefix(location.pathname).startsWith(ROUTE_NAMES.admin.management);

  const variant = useMemo(
    () =>
      authenticationKey
        ? /* istanbul ignore next -- @preserve */ 'single-page'
        : 'primary',
    [authenticationKey],
  );

  const {
    isDealerGroup,
    isFleet,
    isDealerTeam,
    oemSegment,
    belongsToOemSegment,
  } = useSentinelTeam();
  const {
    id,
    isAdmin,
    isSuperUser,
    isOemManager,
    oemSegments,
    managedSentinelTeams,
    needsSentinelTeam,
    availableAssetViews,
  } = useCurrentUserContext();

  const [{ isExpanded, navExpandedState }, handleChangeNavExpandedState] =
    useStickyLeftNav({
      getPreferredNavExpandedState: () =>
        memoryDB.getItem('navExpandedState') ?? undefined,
      setPreferredNavExpandedState: /* istanbul ignore next -- @preserve */ (
        state,
      ) =>
        memoryDB[state ? 'setItem' : 'removeItem']('navExpandedState', state),
    });

  const canViewAdminCaseUrls =
    isAdmin && belongsToOemSegment && !needsSentinelTeam;

  const { data: adminUrlsData } = useCaseAdminUrlsQuery({
    skip: !canViewAdminCaseUrls,
  });

  const caseAdminUrls = useMemo(
    () => adminUrlsData?.currentUserProfile?.user?.caseAdminUrls || [],
    [adminUrlsData],
  );

  const caseAdminUrlsItems = useMemo(() => {
    return caseAdminUrls.map((caseUrl) => ({
      url: caseUrl.url,
      target: '_blank',
      text: t`Tools for ${caseUrl.productName}`,
    }));
  }, [caseAdminUrls]);

  const assetViews = useMemo(() => {
    /* istanbul ignore next -- @preserve */
    if (isEmpty(availableAssetViews)) return undefined;

    return availableAssetViews.map((assetView) => ({
      text: assetView.name,
      path: `/assets/${assetView.slug}`,
    }));
  }, [availableAssetViews]);

  const assetViewItems = useMemo(
    () =>
      assetViews && assetViewsEnabled
        ? { items: assetViews }
        : /* istanbul ignore next -- @preserve */ {},
    [assetViews, assetViewsEnabled],
  );

  const partialAdminAccess = oemSegment?.settings?.partialAdminAccess ?? false;
  const hasFullAdminAccess = isSuperUser || !partialAdminAccess;
  const showInsiteReports = oemSegment?.settings?.showInsiteReports ?? false;

  const hasMultipleOemSegments = isOemManager && oemSegments?.length > 1;
  const showOemSegmentsItem = isSuperUser || hasMultipleOemSegments;
  const showTeamsItem = isSuperUser || managedSentinelTeams?.length > 1;

  const managementItems = useMemo(() => {
    return [
      showOemSegmentsItem && {
        icon: Grid,
        text: t`OEM Segments`,
        path: ROUTE_NAMES.admin.managementOemSegments,
      },
      showTeamsItem && {
        icon: UsersGroup,
        text: t`Teams`,
        path: ROUTE_NAMES.admin.managementTeams,
      },
      {
        icon: User,
        text: t`Accounts`,
        path: ROUTE_NAMES.admin.managementAccounts,
      },
      {
        icon: Building,
        text: t`Depots`,
        path: ROUTE_NAMES.admin.managementDepots,
      },
    ].filter(Boolean);
  }, [showOemSegmentsItem, showTeamsItem]);

  // When the user navigates via "Back to All Teams" they land on team-less
  // routes (no /t/:teamKey prefix). Detect this so we show the superadmin/
  // manager nav instead of the team-scoped nav.
  const knownTeamlessRoutes = [
    ROUTE_NAMES.teams,
    ROUTE_NAMES.admin.oem_segments,
    ROUTE_NAMES.admin.management,
    ROUTE_NAMES.superadmin.oem_onboard,
    ROUTE_NAMES.superadmin.reports,
  ];
  const isOutsideTeamScope =
    !urlTeamKey &&
    knownTeamlessRoutes.some((r) => location.pathname.startsWith(r));

  const items = useMemo(() => {
    /* istanbul ignore next -- @preserve */
    if (!id) {
      return [];
    }

    if (needsSentinelTeam || isOutsideTeamScope) {
      const showManagementPage =
        !pd1188FFEnabled && (isSuperUser || isOemManager);
      return [
        isSuperUser && {
          icon: UsersGroup,
          path: ROUTE_NAMES.teams,
          text: t`Teams`,
        },
        {
          icon: Building,
          path: ROUTE_NAMES.admin.oem_segments,
          text: t`Oem Segments`,
        },
        showManagementPage && {
          icon: List,
          path: ROUTE_NAMES.admin.management,
          text: t`Management`,
        },
        isSuperUser && {
          icon: Folder,
          path: ROUTE_NAMES.superadmin.oem_onboard,
          text: t`OemSegments Onboarding`,
        },
        isSuperUser &&
          !showInsiteReports && {
            icon: BarChart,
            path: ROUTE_NAMES.superadmin.reports,
            text: t`Reports`,
          },
      ].filter(Boolean);
    }

    const assetGroupingItem = isFleet
      ? /* istanbul ignore next -- @preserve */ fleetAssetGroupingItem
      : customerAssetGroupingItem;

    const showServiceLocations =
      hasFullAdminAccess && (isDealerGroup || isDealerTeam);

    const adminSubItems = [
      ...caseAdminUrlsItems,
      hasFullAdminAccess && {
        text: t`Account`,
        path: ROUTE_NAMES.admin.account,
      },
      {
        text: t`Members`,
        path: ROUTE_NAMES.admin.members,
      },
      showServiceLocations && {
        text: t`Service Locations`,
        path: ROUTE_NAMES.admin.service_locations,
      },
      hasFullAdminAccess && {
        text: t`Integrations`,
        path: ROUTE_NAMES.admin.integrations,
      },
      {
        text: t`Preferences`,
        path: ROUTE_NAMES.admin.preferences,
      },
    ].filter(Boolean);

    const adminItems = isAdmin
      ? [
          {
            icon: Cog,
            text: t`Administration`,
            ...(hasFullAdminAccess ? { path: ROUTE_NAMES.admin.root } : {}),
            items: adminSubItems,
          },
        ]
      : [];

    return [
      {
        icon: Home,
        path: ROUTE_NAMES.dashboard,
        text: t`Home`,
      },
      {
        icon: Truck,
        path: ROUTE_NAMES.alerts,
        text: t`Assets`,
        ...assetViewItems,
      },
      {
        icon: Calendar,
        path: ROUTE_NAMES.service_events,
        text: t`Service Events`,
      },
      assetGroupingItem,
      !showInsiteReports && {
        icon: BarChart,
        path: ROUTE_NAMES.reports,
        text: t`Reports`,
      },
      ...adminItems,
    ].filter(Boolean);
  }, [
    id,
    needsSentinelTeam,
    isOutsideTeamScope,
    pd1188FFEnabled,
    isFleet,
    isAdmin,
    isOemManager,
    hasFullAdminAccess,
    isDealerGroup,
    isDealerTeam,
    showInsiteReports,
    assetViewItems,
    isSuperUser,
    caseAdminUrlsItems,
  ]);

  // Prefix all nav item paths with the team key for team-scoped navigation
  const prefixItemPaths = (navItems) =>
    navItems.map((item) => ({
      ...item,
      ...(item.path ? { path: teamPath(item.path) } : {}),
      ...(item.items ? { items: prefixItemPaths(item.items) } : {}),
    }));

  const teamItems = useMemo(
    () => prefixItemPaths(items),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, teamPath],
  );

  return (
    <PageContainer>
      <FaviconManager />
      {variant === 'primary' && (
        <NavColumnContainer>
          <LeftNavContainer>
            <LeftNav
              currentPath={window.location.pathname}
              items={(() => {
                if (isManagementSection) return managementItems;
                if (isOutsideTeamScope) return items;
                return teamItems;
              })()}
              navExpandedState={navExpandedState}
              onChangeNavExpandedState={handleChangeNavExpandedState}
            />
          </LeftNavContainer>
          {isExpanded && <Versions />}
        </NavColumnContainer>
      )}
      <PrimaryContentContainer>
        <TopNavContainer>
          <TopNav />
        </TopNavContainer>
        <ScrollingContent>
          <Outlet />
        </ScrollingContent>
      </PrimaryContentContainer>
    </PageContainer>
  );
}

export default Layout;
